/**
 * this AppRouter contains
 * the core logic and routing for the app
 *
 * we are using react-router v7 in data mode
 * so, all routes are defined here
 *
 * all loaders, and router related component and logic
 * is exported from here
 *
 *
 */
import React, { useEffect, memo, useMemo } from "react";
import i18n from "i18next";

import { arrSigninAsAdminAuthId, BASE_URL, IS_LOCALHOST } from "@/config/Constants";
import {
  createBrowserRouter,
  useHref,
  redirect,
  Outlet,
  useLoaderData,
  useSearchParams,
  useParams,
  RouterProvider,
} from "react-router";
import { RouteMap, URLAccessibleWithoutSession } from "@/config/RouteMap";
import NotFound, { ErrorFallbackComponent } from "@/pages/NotFound";
import { SessionContextProvider } from "@/contexts/sessionContext";
import { routerSessionContext } from "@/contexts/routerContext";
import { AESGCM_DECRYPT } from "@/utils/aes-256-GCM";
import { Session } from "@/session";
import Logout from "@/components/inc/Logout";
import { cleanURLParam, getSessionDataNew, updateDashboardLanguage } from "@/services/sessionServices";
import { RootLayout } from "@/App";
import { FullPageLoadingLayout, JSPageLayout, LoadingLayout, LoginPageLayout } from "@/components/Layouts";

/**
 * this is mounted at /redirect/* path
 * it will redirect to the external link provided in the location state
 *
 * WHY? - in settings/jobs-config, we need to show the unsaved changes warning
 * if the user tries to navigate away, but it only works in Link/ naviagate of react-router
 *
 * in PHP pages, and language change reload, we cant use Link/navigate
 * so, we navigate to this component first, which triggers the unsaved changes warning
 *
 * after user confirms, we redirect to the external link
 * uses window.location.replace to avoid back button issues
 *
 * replaced by the loader redirect logic now - externalPageRouterLoader
 */

const externalPageRouterLoader = async ({ request, params }) => {
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  window.location.href = "/" + params["*"] + (searchParams.toString() ? `?${searchParams.toString()}` : "");
  return new Promise(() => {});
};
/**
 * we need to reload the page on lanuage change
 * but there might be unsaved settings on users side
 * so, we navigate the user to a route, which has this component
 * the navigate triggers the blocker
 *
 * after successful language change on server side
 * we reload the page to the same route, so that all components are loaded in new language
 *
 * we were updating Session earlier to the new language,
 * we now dont need that, as we load session on index load
 *
 * UPDATE - this is replaced by the loader changeLanguageRouteLoader now - will soon be removed
 * UPDATE 2 - converted to component as updating session might take time, so we show loading layout meanwhile
 *
 */
const ChangeLanguage = memo(() => {
  const [searchParams] = useSearchParams();
  const redirect = useMemo(() => {
    const redirectParam = searchParams.get("redirect");
    try {
      const decodedRedirect = atob(redirectParam);
      return decodedRedirect;
    } catch (error) {
      
    }
    return "/";
  }, [searchParams]);

  const reloadHref = useHref(redirect);
  const params = useParams();
  const setLanguage = params["*"] === "en" ? "en" : "de";

  useEffect(() => {
    localStorage.setItem("login_language", setLanguage);
    updateDashboardLanguage(setLanguage).then(() => {
      window.location.replace(reloadHref);
    });
    // eslint-disable-next-line
  }, []);


  return <FullPageLoadingLayout lang={setLanguage} />;
});

/**
 * Helper function to decode and validate redirect URL
 * this parses the URL param 'redirect', decodes from base64
 * and checks if it starts with '/' and is not just '/'
 * if valid, returns the decoded URL, else returns the default URL of the user
 *
 * splitted for reusability
 */
const getValidRedirectUrl = (redirectParam, defaultUrl) => {
  if (!redirectParam) return defaultUrl;

  try {
    const decodedRedirect = atob(redirectParam);
    if (decodedRedirect.startsWith("/") && decodedRedirect !== "/") {
      return decodedRedirect;
    }
  } catch (error) {
  }

  return defaultUrl;
};

/**
 * this is an async loader function for login route
 * it checks if user is already logged in, if yes then redirect to /myjobs OR
 * or to the redirect param provided in the url
 *
 * if not logged in then just return empty object
 */
const loginRootLoader = async ({ request }) => {
  const noSessionRefer = sessionStorage.getItem("noSessionRefer") === "true";
  sessionStorage.removeItem("noSessionRefer");

  if (noSessionRefer) {
    return {};
  }

  let loginStatus = {};
  try {
    loginStatus = await getSessionDataNew({ checkLoginOnly: true });
  } catch (err) {
  }

  if (loginStatus.success && loginStatus?.status === "logged_in") {
    const url = new URL(request.url);
    const redirectParam = url.searchParams.get("redirect");
    const defaultUrl = loginStatus.redirect || BASE_URL + "/myjobs";

    const redirectUrl = getValidRedirectUrl(redirectParam, defaultUrl);

    if (redirectUrl.includes(BASE_URL)) {
      throw redirect(redirectUrl.replace(BASE_URL, ""));
    }

    window.location.replace(redirectUrl);
    return new Promise(() => {});
  }

  return {};
};

/**
 * we have unsubscribe and not found or error pages inside the session wrapper
 * but, we also need to show these pages, even when no session is found
 *
 * here we define the routes for that
 */
const noSessionSkipRoutes = [RouteMap.unsubscribe];
const allValidRoutes = [...Object.values(RouteMap)];

let sessionDataPromise = null;

/**
 * Helper function to fetch and decrypt session data
 *
 * splitted for reusability
 */
const fetchSessionData = async () => {
  if (sessionDataPromise) return sessionDataPromise;

  sessionDataPromise = (async () => {
    try {
      const sessionData = await getSessionDataNew();
      const decryptedString = AESGCM_DECRYPT(sessionData.session || "");
      sessionData.session = JSON.parse(decryptedString);
      return sessionData;
    } catch (err) {
      return { status: "error", ...err };
    }
  })();

  // clear cache after 2 seconds
  sessionDataPromise.finally(() => {
    setTimeout(() => {
      sessionDataPromise = null;
    }, 2000);
  });

  return sessionDataPromise;
};

/**
 * Helper function to handle session errors
 */
const handleSessionError = (sessionData) => {
  if (sessionData?.status === "error" && sessionData?.error === "invalid_userid") {
    throw new Response(JSON.stringify({ hasSession: !!sessionData?.has_session }), {
      status: 404,
      statusText: "Not Found",
    });
  }

  if (sessionData?.status === "error" && sessionData?.error === "broken_link") {
    throw new Response(JSON.stringify({ hasSession: !!sessionData?.has_session }), {
      status: 400,
      statusText: "Bad Request",
    });
  }

  /**
   * the below block needs to be moved out from here
   * as the above blocks were supposed to throw errors codes only
   */
  if (sessionData?.status === "error" && sessionData?.error === "no_manage_session") {
    Session.destroy();
    window.localStorage.removeItem("login");
    sessionStorage.setItem("noSessionRefer", "true");

    throw redirect(URLAccessibleWithoutSession.login);
  }
};

/**
 * helper function to normalize pathname from URL
 */
const normalizePathname = (url) => {
  return url.pathname.replace(BASE_URL, "").replace(/^\//, "");
};

/**
 * helper function to handle no session scenario
 */
const handleNoSession = (pathname, url, context) => {
  const shouldSkipSessionCheck = noSessionSkipRoutes.some((route) => pathname.includes(route));
  

  /**
   * this is the case of invalid routes, we show a 404 page for them, 404 page with empty session
   */
  if (!allValidRoutes.some((route) => pathname === route)) {
    context.set(routerSessionContext, { session: {} });
    return { session: {} };
  }

  /**
   * this is the case of unsubscribe route, we show the page even when no session is found
   * as navigating to and from this unsubscribe page is through window.location
   * and not via router, the loaders will be triggered again
   */
  if (shouldSkipSessionCheck) {
    context.set(routerSessionContext, { session: {} });
    return { session: {} };
  }

  // Redirect to login with return URL
  const redirectPathname = normalizePathname(url);
  const redirectParam = btoa(redirectPathname + url.search);
  let loginUrl = URLAccessibleWithoutSession.login;
  if (redirectParam) {
    loginUrl += "?redirect=" + encodeURIComponent(redirectParam);
  }

  sessionStorage.setItem("noSessionRefer", "true");
  context.set(routerSessionContext, { noSessionRefer: true });
  throw redirect(loginUrl);
};

/**
 * for simplify dashboard
 */
export const getDefaultLandingPageURL = (landingPageString) => {
  return RouteMap.ticket_module_admin;
};

/**
 * Helper function to handle user ID validation and redirection
 *
 * if the theuserid param in URL does not match the SignInUserID from session
 * we redirect to the same URL with correct theuserid param
 *
 * also handles index route (empty pathname) by redirecting to default landing page
 */
const validateAndRedirectUserId = (session, url) => {
  const UserID = session.UserID || "";
  const SignInUserID = session.SignInUserID || "";
  const currentTheUserIdParam = cleanURLParam(url.searchParams.get("theuserid"));
  const requestPathname = normalizePathname(url);

  // new PDO changes, strings changes to numbers
  // console.log("aaaaa33", { UserID, SignInUserID, currentTheUserIdParam, requestPathname });

  const needsUserIdFix = UserID !== SignInUserID && currentTheUserIdParam !== btoa(SignInUserID);
  const isIndexRoute = requestPathname === "";

  if (!needsUserIdFix && !isIndexRoute) {
    return;
  }

  let pathname = requestPathname;
  if (isIndexRoute) {
    pathname = getDefaultLandingPageURL();
  }

  // build the redirect URL with corrected theuserid if needed
  const redirectUrl = new URL(url);
  if (needsUserIdFix) {
    redirectUrl.searchParams.set("theuserid", btoa(SignInUserID));
  }

  const redirectPath = pathname + redirectUrl.search;
  throw redirect(redirectPath);
};

/**
 * Helper function to initialize session and language
 */
const initializeSessionAndLanguage = (session, context) => {
  Session.start({ payload: { ...session } });
  /** for react_app support */
  const dashboardLanguage = (session.DashboardLanguage || "DE").toLowerCase();
  i18n.changeLanguage(dashboardLanguage);
  localStorage.setItem("login_language", dashboardLanguage);
  localStorage.setItem("hasSessionProvider", true); // for the sake of react_app
  context.set(routerSessionContext, { session: session || {} });
};

/**
 * this is an async loader function for js page layout route
 * it checks if user is logged in, if not then redirect to login page
 * if logged in then check if theuserid param is correct, if not then redirect to the correct theuserid param
 * if everything is fine then just return empty object
 */
const privateRouteSessionLoader = async ({ request, context }) => {
  const sessionData = await fetchSessionData();

  

  // session errors (404, 400, etc.)
  handleSessionError(sessionData);

  const url = new URL(request.url);
  const pathname = normalizePathname(url);

  // not logged in cases
  if (!sessionData.success || sessionData?.status !== "logged_in") {
    return handleNoSession(pathname, url, context);
  }

  // valid logged-in session
  if (sessionData.success && sessionData?.status === "logged_in" && sessionData.session) {
    validateAndRedirectUserId(sessionData.session, url);
  }

  // set to session, and update language from session
  initializeSessionAndLanguage(sessionData.session, context);

  return { session: sessionData.session || {} };
};

const NestedSessionContextProvider = memo(({ initMemoizedSession }) => {
  
  return (
    <SessionContextProvider initSession={initMemoizedSession}>
      <Outlet />
    </SessionContextProvider>
  );
});
NestedSessionContextProvider.displayName = "NestedSessionContextProvider";

/**
 * these routes has the session context provider
 * so that all components inside these routes has access to session context
 */
const PrivateRoutesWrapper = memo(() => {
  const { session } = useLoaderData() || {};
  const memoizedSession = useMemo(() => session, [session]);

  
  return <NestedSessionContextProvider initMemoizedSession={memoizedSession} />;
});
PrivateRoutesWrapper.displayName = "PrivateRoutesWrapper";

const router = createBrowserRouter(
  [
    {
      // path: "",
      id: "root-layout",
      Component: RootLayout,
      HydrateFallback: FullPageLoadingLayout,
      children: [
        {
          // path: "",
          // loader: publicRouteLoader,
          // shouldRevalidate: () => false,
          id: "public-routes",
          children: [
            {
              // path: "",
              id: "js-login-layout",
              ErrorBoundary: ErrorFallbackComponent,
              loader: loginRootLoader,
              shouldRevalidate: () => false,
              Component: LoginPageLayout,
              HydrateFallback: FullPageLoadingLayout,
              children: [
                {
                  path: URLAccessibleWithoutSession.login,
                  lazy: async () => {
                    const Component = await import("./pages/Login");
                    return {
                      Component: Component.default,
                    };
                  },
                  HydrateFallback: FullPageLoadingLayout,
                },
              ],
            },
          ],
        },
        {
          // path: "",
          id: "private-routes",
          loader: privateRouteSessionLoader,
          shouldRevalidate: () => false, // we will be using window location redirects, not router's. we dont need this to revalidate on route change
          ErrorBoundary: ErrorFallbackComponent,
          Component: PrivateRoutesWrapper,
          HydrateFallback: FullPageLoadingLayout,
          children: [
            {
              index: true,
              Component: <></>,
            },
            {
              // path: "",
              Component: JSPageLayout,
              id: "js-page-layout",
              ErrorBoundary: ErrorFallbackComponent,
              HydrateFallback: FullPageLoadingLayout,
              children: [
                {
                  path: RouteMap.postings_overview_admin,
                  lazy: async () => {
                    const Component = await import("./pages/PostingsOverviewAdmin");
                    return { Component: Component.default };
                  },
                  HydrateFallback: LoadingLayout,
                },
                {
                  path: RouteMap.ticket_module_admin,
                  lazy: async () => {
                    const Component = await import("./pages/admin/TicketModuleAdmin");
                    return { Component: Component.default };
                  },
                  HydrateFallback: LoadingLayout,
                },
              ],
            },

            {
              path: "redirect/*",
              loader: externalPageRouterLoader,
              HydrateFallback: FullPageLoadingLayout,
            },
            {
              path: "lang/*",
              // loader: changeLanguageRouteLoader,
              Component: ChangeLanguage,
              HydrateFallback: FullPageLoadingLayout,
            },
            {
              path: "*",
              Component: NotFound,
              HydrateFallback: FullPageLoadingLayout,
            },
          ],
        },
        {
          path: RouteMap.logout,
          Component: Logout,
          HydrateFallback: FullPageLoadingLayout,
        },
      ],
    },
  ],
  {
    basename: BASE_URL,
  }
);

/**
 * React Router v7 working in Data Mode.
 * Shifted from declarative mode to data mode for better features set
 */
const AppRouter = memo(() => {
  useEffect(() => {
    /**
     * on alpha/beta htaccess or php redirects it to kunden_beta/v2
     *
     * but on localhost, react opens to localhost:3001, with no base URL
     * so, if the / is accessed, we redirect to the base URL
     * this is only for localhost
     *
     * dont merge to LIVE
     */
    if (!IS_LOCALHOST) return;

    if (window.location.pathname === "/") {
      window.location = BASE_URL;
    }
  }, []);

  return <RouterProvider router={router} />;
});
AppRouter.displayName = "AppRouter";

export default AppRouter;
